/* eslint-disable no-process-env */

// eslint-disable-next-line @typescript-eslint/no-require-imports
const zod = require('zod')

const clientEnvSchema = zod.object({
  NEXT_PUBLIC_API_URL: zod.string(),
  NEXT_PUBLIC_VERSION: zod.string(),
  NEXT_PUBLIC_DASHBOARD_URL: zod.string(),
  NEXT_PUBLIC_BASE_DOMAIN_URL: zod.string(),
  NEXT_PUBLIC_INTERCOM_APP_ID: zod.string(),
  NEXT_PUBLIC_ASSETS2_URL: zod.string(),
  NEXT_PUBLIC_AUTH_COOKIES_EXPIRY_DAYS: zod
    .string()
    .transform((days) => parseInt(days))
    .refine((expiry) => !isNaN(expiry), {
      message: 'must be a number'
    }),
  NEXT_PUBLIC_API_MOCKING: zod.string().optional().default('disabled'),
  NEXT_PUBLIC_LOGROCKET_APP_ID: zod.string(),
  NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: zod.string(),
  NEXT_PUBLIC_ALVIE_SA_DOMAIN_NAME: zod.string(),
  NEXT_PUBLIC_STATSIG_CLIENT_KEY: zod.string(),
  NEXT_PUBLIC_IDENTITY_PLATFORM_API_KEY: zod.string(),
  NEXT_PUBLIC_IDENTITY_PLATFORM_AUTH_DOMAIN: zod.string(),
  NEXT_PUBLIC_IDENTITY_PLATFORM_PROJECT_ID: zod.string(),
  // Optional. When set (e.g. http://127.0.0.1:5000), analytics is proxied to that Cirrus.
  NEXT_PUBLIC_CIRRUS_URL: zod.string().optional()
})

const serverEnvSchema = zod.object({
  AMAZON_APP_ID: zod.string(),
  AMAZON_APP_SECRET: zod.string(),
  CRITEO_API_ID: zod.string(),
  CRITEO_API_SECRET: zod.string(),
  FACEBOOK_APP_ID: zod.string(),
  FACEBOOK_APP_SECRET: zod.string(),
  FIVETRAN_APP_ID: zod.string(),
  FIVETRAN_APP_SECRET: zod.string(),
  FIVETRAN_DEST_REGION: zod.string(),
  FIVETRAN_DEST_TIME_ZONE_OFFSET: zod.string(),
  FIVETRAN_DEST_BQ_PROJECT_ID: zod.string(),
  FIVETRAN_DEST_BQ_REGION: zod.string(),
  FIVETRAN_DEST_BUCKET: zod.string(),
  SNAPCHAT_APP_ID: zod.string(),
  SNAPCHAT_APP_SECRET: zod.string(),
  TIKTOK_APP_ID: zod.string(),
  TIKTOK_APP_SECRET: zod.string(),
  PINTEREST_APP_ID: zod.string(),
  PINTEREST_APP_SECRET: zod.string(),
  GOOGLE_APP_ID: zod.string(),
  GOOGLE_APP_SECRET: zod.string(),
  LINKEDIN_APP_ID: zod.string(),
  LINKEDIN_APP_SECRET: zod.string(),
  MICROSOFT_ADVERTISING_APP_ID: zod.string(),
  MICROSOFT_ADVERTISING_APP_SECRET: zod.string(),
  API_URL: zod.string(),
  INTERNAL_API_URL: zod.string(),
  MICROSOFT_ADVERTISING_DEVELOPER_TOKEN: zod.string(),
  NODE_ENV: zod.string().default('production'),
  APP_ENV: zod.string().default('production'),
  ROLLBAR_SERVER_TOKEN: zod.string(),
  I18N_DEBUG: zod
    .string()
    .optional()
    .transform((flag) => flag?.trim() === 'true'),
  INTERNAL_API_CLIENT_ID: zod.string(),
  GCP_IMPERSONATE_SERVICE_ACCOUNT: zod.string().optional(),
  GCS_ASSETS_BUCKET: zod.string().optional().default('assets1.alvie.io'),
  GCS_ASSETS_CUSTOM_DOMAIN: zod.string().optional(),
  GCS_CREATIVE_ASSETS_BUCKET: zod.string().optional().default('attaboy-161918.appspot.com'),
  WEBFLOW_API_TOKEN: zod.string(),
  // Must be a full URL — buildFlowHeaders parses the hostname to decide
  // whether to attach a GCP identity token (hosted) or skip it (localhost).
  FLOW_API_URL: zod.string().url()
})

const configs = {
  NEXT_PUBLIC_DASHBOARD_URL: process.env.NEXT_PUBLIC_DASHBOARD_URL,
  NEXT_PUBLIC_BASE_DOMAIN_URL: process.env.NEXT_PUBLIC_BASE_DOMAIN_URL,
  NEXT_PUBLIC_AUTH_COOKIES_EXPIRY_DAYS: process.env.NEXT_PUBLIC_AUTH_COOKIES_EXPIRY_DAYS,
  NEXT_PUBLIC_API_MOCKING: process.env.NEXT_PUBLIC_API_MOCKING,
  NEXT_PUBLIC_VERSION: process.env.NEXT_PUBLIC_VERSION,
  NEXT_PUBLIC_LOGROCKET_APP_ID: process.env.NEXT_PUBLIC_LOGROCKET_APP_ID,
  NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL ?? process.env.API_URL,
  NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
  NEXT_PUBLIC_ALVIE_SA_DOMAIN_NAME: process.env.NEXT_PUBLIC_ALVIE_SA_DOMAIN_NAME,
  NEXT_PUBLIC_STATSIG_CLIENT_KEY: process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY,
  NEXT_PUBLIC_IDENTITY_PLATFORM_API_KEY: process.env.NEXT_PUBLIC_IDENTITY_PLATFORM_API_KEY,
  NEXT_PUBLIC_IDENTITY_PLATFORM_AUTH_DOMAIN: process.env.NEXT_PUBLIC_IDENTITY_PLATFORM_AUTH_DOMAIN,
  NEXT_PUBLIC_IDENTITY_PLATFORM_PROJECT_ID: process.env.NEXT_PUBLIC_IDENTITY_PLATFORM_PROJECT_ID,
  NEXT_PUBLIC_ASSETS2_URL: process.env.NEXT_PUBLIC_ASSETS2_URL,
  NEXT_PUBLIC_CIRRUS_URL: process.env.NEXT_PUBLIC_CIRRUS_URL,
  API_URL: process.env.API_URL ?? process.env.NEXT_PUBLIC_API_URL,
  INTERNAL_API_URL: process.env.INTERNAL_API_URL,
  AMAZON_APP_ID: process.env.AMAZON_APP_ID,
  NODE_ENV: process.env.NODE_ENV,
  APP_ENV: process.env.APP_ENV,
  AMAZON_APP_SECRET: process.env.AMAZON_APP_SECRET,
  CRITEO_API_ID: process.env.CRITEO_API_ID,
  CRITEO_API_SECRET: process.env.CRITEO_API_SECRET,
  FACEBOOK_APP_ID: process.env.FACEBOOK_APP_ID,
  FACEBOOK_APP_SECRET: process.env.FACEBOOK_APP_SECRET,
  FIVETRAN_APP_ID: process.env.FIVETRAN_APP_ID,
  FIVETRAN_APP_SECRET: process.env.FIVETRAN_APP_SECRET,
  FIVETRAN_DEST_REGION: process.env.FIVETRAN_DEST_REGION,
  FIVETRAN_DEST_TIME_ZONE_OFFSET: process.env.FIVETRAN_DEST_TIME_ZONE_OFFSET,
  FIVETRAN_DEST_BQ_PROJECT_ID: process.env.FIVETRAN_DEST_BQ_PROJECT_ID,
  FIVETRAN_DEST_BQ_REGION: process.env.FIVETRAN_DEST_BQ_REGION,
  FIVETRAN_DEST_BUCKET: process.env.FIVETRAN_DEST_BUCKET,
  SNAPCHAT_APP_ID: process.env.SNAPCHAT_APP_ID,
  SNAPCHAT_APP_SECRET: process.env.SNAPCHAT_APP_SECRET,
  TIKTOK_APP_ID: process.env.TIKTOK_APP_ID,
  TIKTOK_APP_SECRET: process.env.TIKTOK_APP_SECRET,
  PINTEREST_APP_ID: process.env.PINTEREST_APP_ID,
  PINTEREST_APP_SECRET: process.env.PINTEREST_APP_SECRET,
  GOOGLE_APP_ID: process.env.GOOGLE_APP_ID,
  GOOGLE_APP_SECRET: process.env.GOOGLE_APP_SECRET,
  LINKEDIN_APP_ID: process.env.LINKEDIN_APP_ID,
  LINKEDIN_APP_SECRET: process.env.LINKEDIN_APP_SECRET,
  MICROSOFT_ADVERTISING_APP_ID: process.env.MICROSOFT_ADVERTISING_APP_ID,
  MICROSOFT_ADVERTISING_APP_SECRET: process.env.MICROSOFT_ADVERTISING_APP_SECRET,
  MICROSOFT_ADVERTISING_DEVELOPER_TOKEN: process.env.MICROSOFT_ADVERTISING_DEVELOPER_TOKEN,
  I18N_DEBUG: process.env.I18N_DEBUG,
  ROLLBAR_SERVER_TOKEN: process.env.ROLLBAR_SERVER_TOKEN,
  INTERNAL_API_CLIENT_ID: process.env.INTERNAL_API_CLIENT_ID,
  GCP_IMPERSONATE_SERVICE_ACCOUNT: process.env.GCP_IMPERSONATE_SERVICE_ACCOUNT,
  GCS_ASSETS_BUCKET: process.env.GCS_ASSETS_BUCKET,
  GCS_ASSETS_CUSTOM_DOMAIN: process.env.GCS_ASSETS_CUSTOM_DOMAIN,
  GCS_CREATIVE_ASSETS_BUCKET: process.env.GCS_CREATIVE_ASSETS_BUCKET,
  WEBFLOW_API_TOKEN: process.env.WEBFLOW_API_TOKEN,
  FLOW_API_URL: process.env.FLOW_API_URL
}

const mergedSchema = serverEnvSchema.merge(clientEnvSchema)
const clientEnvSchemaWithOptionalServerSchema = clientEnvSchema.merge(serverEnvSchema.partial())

const isServer = typeof window === 'undefined'

const isTestOrDevEnvironment = process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development'
const isStagingOrProductionEnvironment = !isTestOrDevEnvironment

// custom env provided in package.json build script
const isRunningNextBuild = process.env.NEXT_APP_BUILD === 'true'

/**
 * Validate all environment configs for test and development environment
 * and before app startup in staging/production. Otherwise, validate client-side envs
 * This is to prevent having to require server-side envs in staging/production configurations before they are available
 */
const parsed =
  isServer && (isTestOrDevEnvironment || (isStagingOrProductionEnvironment && !isRunningNextBuild))
    ? mergedSchema.safeParse(configs)
    : clientEnvSchemaWithOptionalServerSchema.safeParse(configs)

if (!parsed.success) {
  // validation error
  throw new Error('Config validation error \n' + JSON.stringify(parsed.error.flatten().fieldErrors))
}

// Browser analytics cannot call a local Cirrus host directly. Only the dev server
// may send those requests through /api/proxy; deployed builds keep the public API.
const nodeEnv = process.env.NODE_ENV ?? process.env.NEXT_PUBLIC_NODE_ENV
const localCirrusUrl = parsed.data.NEXT_PUBLIC_CIRRUS_URL?.trim() ?? ''

const parsedConfigs = {
  ...parsed.data,
  APP_ENV: process.env.APP_ENV ?? process.env.NEXT_PUBLIC_APP_ENV,
  NODE_ENV: nodeEnv,
  ANALYTICS_REQUEST_BASE_URL:
    nodeEnv === 'development' && localCirrusUrl.length > 0 ? '/api/proxy' : parsed.data.NEXT_PUBLIC_API_URL
}

/**
 * @param {string} key - The name of the environmental variable
 * @return {string | boolean | number}
 */
module.exports = (key) => {
  const value = parsedConfigs[key]

  return value
}
